import {
  OutboundEmailAddressFieldsFragment,
  OutboundEmailFieldsFragment,
  OutboundEmailSortField,
  OutboundEmailSortInput,
  OutboundEmailStatus,
  SortDirection
} from '~/api/graphql';
import { rowDateTimeFormatter } from '~/components/table-formatters';

export type OutboundEmailSortKey = 'status' | 'subject' | 'sent' | 'created';

const SORT_KEY_TO_FIELD: Record<OutboundEmailSortKey, OutboundEmailSortField> = {
  status: OutboundEmailSortField.Status,
  subject: OutboundEmailSortField.Subject,
  sent: OutboundEmailSortField.SentAt,
  created: OutboundEmailSortField.CreatedAt
};

/** Map UI sort key + direction to GraphQL OutboundEmailSortInput (default: CREATED_AT DESC — sentAt is null on anything unresolved). */
export function outboundEmailSortToSortInput(
  sortBy: OutboundEmailSortKey | undefined,
  sortDirection: 'asc' | 'desc'
): OutboundEmailSortInput {
  return {
    field: sortBy ? SORT_KEY_TO_FIELD[sortBy] : OutboundEmailSortField.CreatedAt,
    direction: sortDirection === 'asc' ? SortDirection.Asc : SortDirection.Desc
  };
}

/**
 * Shorter than the server's StuckSendAge (recover_outbound.go), so a row stops
 * claiming to be in flight before recovery writes it off.
 */
export const OUTBOUND_EMAIL_STUCK_AFTER_MS = 5 * 60 * 1000;

export type OutboundEmailProgress =
  | 'scheduled'
  | 'sending'
  | 'interrupted'
  | 'sent'
  | 'failed'
  | 'cancelled';

/**
 * When delivery started: a scheduled email's send time, which may be months
 * after it was created, and the click for Send now.
 */
const sendingSince = (email: Pick<OutboundEmailFieldsFragment, 'scheduledFor' | 'createdAt'>) =>
  new Date(email.scheduledFor ?? email.createdAt);

export const outboundEmailProgress = (
  email: Pick<OutboundEmailFieldsFragment, 'status' | 'createdAt' | 'scheduledFor'>,
  nowMs: number
): OutboundEmailProgress => {
  if (email.status === OutboundEmailStatus.Scheduled) {
    return 'scheduled';
  }
  if (email.status === OutboundEmailStatus.Sent) {
    return 'sent';
  }
  if (email.status === OutboundEmailStatus.Cancelled) {
    return 'cancelled';
  }
  if (email.status === OutboundEmailStatus.Failed) {
    return 'failed';
  }
  return nowMs - sendingSince(email).getTime() >= OUTBOUND_EMAIL_STUCK_AFTER_MS
    ? 'interrupted'
    : 'sending';
};

/**
 * Gates the list's polling. Derived progress, not stored status: a row
 * abandoned by a dead sender stays SENDING forever, and would poll forever.
 */
export const hasUnresolvedOutboundEmails = (
  emails: Pick<OutboundEmailFieldsFragment, 'status' | 'createdAt' | 'scheduledFor'>[],
  nowMs: number
): boolean => emails.some((email) => outboundEmailProgress(email, nowMs) === 'sending');

export const formatOutboundEmailElapsed = (ms: number): string => {
  const minutes = Math.max(Math.floor(ms / 60_000), 1);
  if (minutes < 60) {
    return `${minutes}m`;
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours}h`;
  }
  return `${Math.floor(hours / 24)}d`;
};

/** The line under the subject; null for 'sent' and 'sending', which need none. */
export const outboundEmailStatusNote = (
  email: Pick<
    OutboundEmailFieldsFragment,
    'status' | 'createdAt' | 'scheduledFor' | 'failureReason'
  >,
  nowMs: number
): string | null => {
  const progress = outboundEmailProgress(email, nowMs);
  if (progress === 'failed') {
    return email.failureReason.trim() || 'The provider rejected the send without saying why.';
  }
  if (progress === 'interrupted') {
    const elapsed = formatOutboundEmailElapsed(nowMs - sendingSince(email).getTime());
    return `Unresolved for ${elapsed}. The send was interrupted, so it may or may not have reached the recipient.`;
  }
  return null;
};

/** A send's address, or an email artifact's, which carries the same two fields. */
type RecipientAddress = Pick<OutboundEmailAddressFieldsFragment, 'name' | 'email'>;
type Recipients = { to: RecipientAddress[]; cc: RecipientAddress[]; bcc: RecipientAddress[] };

export const formatOutboundEmailAddress = (address: RecipientAddress): string =>
  address.name?.trim() || address.email;

/** Includes bcc: this is the agency's record of who was mailed. */
export const outboundEmailRecipients = (email: Recipients): RecipientAddress[] => [
  ...email.to,
  ...email.cc,
  ...email.bcc
];

export const outboundEmailRecipientSummary = (
  email: Recipients
): { label: string; extra: number; title: string } => {
  const all = outboundEmailRecipients(email);
  return {
    label: all.length > 0 ? formatOutboundEmailAddress(all[0]) : '—',
    extra: Math.max(all.length - 1, 0),
    title: all.map((address) => address.email).join(', ')
  };
};

/** Reopens the exact artifact version that went out; undefined when the send has no workspace. */
export const outboundEmailArtifactHref = (
  email: Pick<
    OutboundEmailFieldsFragment,
    'workspaceId' | 'workspaceArtifactId' | 'artifactVersion'
  >
): string | undefined =>
  email.workspaceId
    ? `/app/workspaces/${email.workspaceId}?artifact_id=${email.workspaceArtifactId}&version=${email.artifactVersion}`
    : undefined;

export const outboundEmailTimestamp = (
  email: Pick<OutboundEmailFieldsFragment, 'sentAt' | 'createdAt'>
): { label: string; title: string; claimedOnly: boolean } =>
  email.sentAt
    ? {
        label: rowDateTimeFormatter.format(new Date(email.sentAt)),
        title: 'Accepted by the mail provider',
        claimedOnly: false
      }
    : {
        label: rowDateTimeFormatter.format(new Date(email.createdAt)),
        title: 'When send was clicked. This email was never confirmed sent.',
        claimedOnly: true
      };
